import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { useLanguage } from '../i18n/i18n';
import './datefield.css';

type Props = {
  id?: string;
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
};

function toDate(iso: string | null | undefined): Date | undefined {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return undefined;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseTyped(text: string): string | null {
  const s = text.trim();
  if (!s) return null;
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) return valid(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(\d{1,2})[/.\-\s](\d{1,2})[/.\-\s](\d{2}|\d{4})$/.exec(s);
  if (m) {
    const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    return valid(y, Number(m[2]), Number(m[1]));
  }
  return null;
}

function valid(y: number, m: number, d: number): string | null {
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return toIso(dt);
}

export default function DateField({ id, value, onChange, min, max, required, disabled, placeholder, className, ...aria }: Props) {
  const { lang, t } = useLanguage();
  const auto = useId();
  const inputId = id ?? `date-${auto}`;
  const popId = `${inputId}-calendar`;
  const locale = lang === 'en' ? 'en-GB' : lang;
  const long = useMemo(() => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }), [locale]);
  const caption = useMemo(() => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }), [locale]);
  const weekday = useMemo(() => new Intl.DateTimeFormat(locale, { weekday: 'short' }), [locale]);
  const full = useMemo(() => new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }), [locale]);
  const monthName = useMemo(() => new Intl.DateTimeFormat(locale, { month: 'long' }), [locale]);

  const selected = toDate(value);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);
  const [month, setMonth] = useState<Date>(selected ?? new Date());
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const keyboardOpen = useRef(false);

  useEffect(() => { if (!editing) setText(selected ? long.format(selected) : ''); }, [value, long, editing]);
  function openPicker(fromKeyboard: boolean) {
    keyboardOpen.current = fromKeyboard;
    setMonth(selected ?? toDate(min) ?? new Date());
    setOpen(true);
  }

  useEffect(() => {
    if (!open || !keyboardOpen.current) return;
    const id = requestAnimationFrame(() => {
      const pop = wrapRef.current?.querySelector('.datefield-pop');
      const day = pop?.querySelector<HTMLButtonElement>('.rdp-day_button[tabindex="0"]') ?? pop?.querySelector<HTMLButtonElement>('.rdp-day_button:not([disabled])');
      day?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
    };
  }, [open]);

  function choose(d: Date | undefined) {
    if (!d) return;
    onChange(toIso(d));
    setEditing(false);
    setOpen(false);
    inputRef.current?.focus();
  }

  function commitTyped() {
    setEditing(false);
    if (!text.trim()) {
      if (!required) onChange(null);
      return;
    }
    const iso = parseTyped(text);
    if (iso && (!min || iso >= min) && (!max || iso <= max)) onChange(iso);
    else setText(selected ? long.format(selected) : '');
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || (e.key === 'Enter' && !editing)) {
      e.preventDefault();
      openPicker(true);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitTyped();
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
    }
  }

  const today = new Date();
  const todayIso = toIso(today);
  const todayAllowed = (!min || todayIso >= min) && (!max || todayIso <= max);
  const minDate = toDate(min);
  const maxDate = toDate(max);
  const disabledDays = [...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])];
  const startMonth = minDate ?? new Date(today.getFullYear() - 100, 0, 1);
  const endMonth = maxDate ?? new Date(today.getFullYear() + 10, 11, 31);

  return (
    <div className={`datefield${className ? ` ${className}` : ''}`} ref={wrapRef}>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className="datefield-input"
        value={text}
        placeholder={placeholder ?? t('Choose a date')}
        required={required}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        aria-label={aria['aria-label']}
        aria-describedby={aria['aria-describedby']}
        onClick={() => { if (!disabled && !open) openPicker(false); }}
        onChange={(e) => { setEditing(true); setText(e.target.value); }}
        onBlur={() => { if (editing) commitTyped(); }}
        onKeyDown={onKey}
      />
      <button
        type="button"
        className="datefield-toggle"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
        onClick={() => { if (open) setOpen(false); else openPicker(false); inputRef.current?.focus(); }}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 6h16v14H4zM4 10h16M8 3v5M16 3v5" />
        </svg>
      </button>
      {open && (
        <div
          id={popId}
          className="datefield-pop"
          role="dialog"
          aria-label={t('Choose a date')}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); inputRef.current?.focus(); } }}
        >
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={choose}
            month={month}
            onMonthChange={setMonth}
            weekStartsOn={1}
            showOutsideDays
            fixedWeeks
            dir={document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr'}
            captionLayout="dropdown"
            startMonth={startMonth}
            endMonth={endMonth}
            disabled={disabledDays}
            formatters={{
              formatCaption: (d) => caption.format(d),
              formatWeekdayName: (d) => weekday.format(d),
              formatMonthDropdown: (d) => monthName.format(d),
              formatYearDropdown: (d) => String(d.getFullYear()),
            }}
            labels={{
              labelDayButton: (d, mods) => `${full.format(d)}${mods?.selected ? `, ${t('selected')}` : ''}${mods?.today ? `, ${t('today')}` : ''}`,
              labelNext: () => t('Next month'),
              labelPrevious: () => t('Previous month'),
              labelMonthDropdown: () => t('Month'),
              labelYearDropdown: () => t('Year'),
            }}
          />
          <div className="datefield-actions">
            {todayAllowed && <button type="button" className="btn secondary" onClick={() => choose(today)}>{t('Today')}</button>}
            {!required && value && <button type="button" className="btn ghost" onClick={() => { onChange(null); setOpen(false); inputRef.current?.focus(); }}>{t('Clear')}</button>}
            <button type="button" className="btn ghost" onClick={() => { setOpen(false); inputRef.current?.focus(); }}>{t('Close')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
